import {
    useAddContentCommentMutation,
    useDeleteContentCommentMutation,
    useGetContentCommentsQuery
} from "@/graphql/generated";
import { AnonymousCommentData, ContentComment } from "@/types/content-comment";
import { maskProfanity } from "@/utils/profanityFilter";
import wordListData from "@/word_list.json";
import { useCallback, useMemo } from "react";

// 전체 비속어 목록 (클라이언트에서 사전 마스킹용)
const PROFANITY_WORD_LIST: string[] = wordListData.words;

interface UseContentCommentsOptions {
    contentId: string;
    limit?: number;
    offset?: number;
    skip?: boolean;
}

interface UseContentCommentsReturn {
    comments: ContentComment[];
    totalCount: number;
    isLoading: boolean;
    addComment: (
        text: string,
        anonymousData?: AnonymousCommentData,
        parentCommentId?: string
    ) => Promise<void>;
    deleteComment: (commentId: string) => Promise<void>;
    toggleReaction: (commentId: string) => Promise<void>;
    removeReaction: (commentId: string) => Promise<void>;
    refetch: () => void;
}

/**
 * 콘텐츠 댓글 관리 훅
 * 댓글 조회, 추가, 삭제, 반응 기능 제공 (n-level 중첩 지원)
 *
 * NOTE: 반응(reaction) 기능과 parent_comment_id는 migration 016 적용 후 활성화됨
 * 현재는 기본 댓글 기능만 동작
 */
export function useContentComments({
    contentId,
    limit = 100,
    offset = 0,
    skip = false
}: UseContentCommentsOptions): UseContentCommentsReturn {
    // 댓글 조회
    const { data, loading, refetch } = useGetContentCommentsQuery({
        variables: { contentId, limit, offset },
        skip: !contentId || skip,
        fetchPolicy: "cache-and-network"
    });

    // 댓글에 필요한 필드 추가 (migration 적용 전 호환성)
    const commentsWithDefaults = useMemo(() => {
        return (data?.comments || []).map((comment) => ({
            ...comment,
            // migration 016 적용 전에는 이 필드들이 없을 수 있음
            parent_comment_id:
                (comment as ContentComment).parent_comment_id ?? null,
            reply_count: (comment as ContentComment).reply_count ?? 0,
            hasReacted: false // 반응 조회는 migration 후 활성화
        })) as ContentComment[];
    }, [data?.comments]);

    // 댓글 추가 mutation
    const [addCommentMutation] = useAddContentCommentMutation();

    // 댓글 삭제 mutation
    const [deleteCommentMutation] = useDeleteContentCommentMutation();

    // 댓글 추가 핸들러 (답글 지원 - migration 후 활성화)
    // 클라이언트에서 비속어 사전 마스킹 후 서버에 전송
    const addComment = useCallback(
        async (
            text: string,
            anonymousData?: AnonymousCommentData,
            parentCommentId?: string
        ) => {
            if (!text.trim()) {
                throw new Error("댓글 내용을 입력해주세요.");
            }

            // 클라이언트에서 비속어 마스킹 처리 (즉각적인 UX)
            const { maskedText } = maskProfanity(
                text.trim(),
                PROFANITY_WORD_LIST
            );

            // NOTE: parentCommentId는 migration 016 적용 후 사용 가능
            // 현재 GraphQL schema에 없으면 무시됨
            const variables: {
                contentId: string;
                text: string;
                isAnonymous: boolean;
                anonymousNickname?: string;
                anonymousAvatarSeed?: string;
                parentCommentId?: string | null;
            } = {
                contentId,
                text: maskedText, // 마스킹된 텍스트 사용
                isAnonymous: anonymousData?.isAnonymous || false,
                anonymousNickname: anonymousData?.anonymousNickname,
                anonymousAvatarSeed: anonymousData?.anonymousAvatarSeed
            };

            // parentCommentId가 있으면 추가 (migration 후)
            if (parentCommentId) {
                variables.parentCommentId = parentCommentId;
            }

            await addCommentMutation({
                variables: {
                    contentId: variables.contentId,
                    text: variables.text,
                    parentCommentId: variables.parentCommentId,
                    isAnonymous: variables.isAnonymous,
                    anonymousNickname: variables.anonymousNickname,
                    anonymousAvatarSeed: variables.anonymousAvatarSeed
                },
                refetchQueries: ["GetContentComments"],
                awaitRefetchQueries: true
            });
        },
        [addCommentMutation, contentId]
    );

    // 댓글 삭제 핸들러
    const deleteComment = useCallback(
        async (commentId: string) => {
            await deleteCommentMutation({
                variables: { commentId },
                refetchQueries: ["GetContentComments"],
                awaitRefetchQueries: true
            });
        },
        [deleteCommentMutation]
    );

    // 반응 토글 핸들러 (migration 016 적용 후 활성화)
    // TODO: useToggleContentCommentReactionMutation 사용
    const toggleReaction = useCallback(async (commentId: string) => {
        console.log(
            "toggleReaction:",
            commentId,
            "- migration 016 적용 후 활성화됨"
        );
        // Migration 적용 후 아래 코드 활성화:
        // await toggleReactionMutation({
        //     variables: { commentId },
        //     refetchQueries: ["GetContentComments", "GetMyContentCommentReactions"],
        //     awaitRefetchQueries: true
        // });
    }, []);

    // 반응 제거 핸들러 (migration 016 적용 후 활성화)
    // TODO: useRemoveContentCommentReactionMutation 사용
    const removeReaction = useCallback(async (commentId: string) => {
        console.log(
            "removeReaction:",
            commentId,
            "- migration 016 적용 후 활성화됨"
        );
        // Migration 적용 후 아래 코드 활성화:
        // await removeReactionMutation({
        //     variables: { commentId },
        //     refetchQueries: ["GetContentComments", "GetMyContentCommentReactions"],
        //     awaitRefetchQueries: true
        // });
    }, []);

    return {
        comments: commentsWithDefaults,
        totalCount: data?.comments_aggregate?.aggregate?.count || 0,
        isLoading: loading,
        addComment,
        deleteComment,
        toggleReaction,
        removeReaction,
        refetch
    };
}
